import { getApps, initializeApp } from 'firebase/app'
import {
  browserPopupRedirectResolver,
  getAdditionalUserInfo,
  getAuth,
  GoogleAuthProvider,
  inMemoryPersistence,
  initializeAuth,
  signInWithCredential,
  signInWithPopup,
  signOut,
  type OAuthCredential,
} from 'firebase/auth'
import { auth, firebaseConfig } from './firebase'
import { rememberLastUsedMethod } from './lastUsedMethod'

export type GoogleSignup = { credential: OAuthCredential; name: string | null }

const SIGNUP_APP_NAME = 'google-signup'

function isolatedAuth() {
  const app = getApps().find(({ name }) => name === SIGNUP_APP_NAME)
  if (app) return getAuth(app)
  return initializeAuth(initializeApp(firebaseConfig, SIGNUP_APP_NAME), {
    persistence: inMemoryPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  })
}

// We create a throwaway auth instead of main auth until user completes signup to avoid partial signups.
export async function signInWithGoogle(): Promise<GoogleSignup | null> {
  const signupAuth = isolatedAuth()
  const result = await signInWithPopup(signupAuth, new GoogleAuthProvider())
  const credential = GoogleAuthProvider.credentialFromResult(result)
  if (!credential) throw new Error('Google did not return a credential.')

  if (getAdditionalUserInfo(result)?.isNewUser) {
    const { displayName } = result.user
    await result.user.delete()
    return { credential, name: displayName }
  }

  await signOut(signupAuth)
  await signInWithGoogleCredential(credential)
  return null
}

export async function signInWithGoogleCredential(credential: OAuthCredential) {
  await signInWithCredential(auth, credential)
  rememberLastUsedMethod('google')
}
